import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Typography } from "@mui/material";
import { Box } from "@mui/system";
import { format, isValid, parseISO, startOfMonth, subMonths } from "date-fns";
import { useTranslation } from "react-i18next";
import type { TooltipProps } from "recharts";
import {
  Bar,
  BarChart,
  CartesianGrid,
  DefaultTooltipContent,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { NameType, ValueType } from "recharts/types/component/DefaultTooltipContent";
import styled, { useTheme } from "styled-components";
import invariant from "ts-invariant";

import { useGetEarningsUsTherapist } from "api/hooks/useGetEarningsUsTherapist";
import { useProfileContext } from "contexts/ProfileContext";
import { ButtonsGroup } from "shared/atoms/ButtonsGroup";
import type { RoundedBarProps } from "shared/atoms/Charts/StackedBarChart/RoundedBar";
import { RoundedBar } from "shared/atoms/Charts/StackedBarChart/RoundedBar";
import { convertCentsToDollars } from "utils/convertCentsToDollars";

import { Container, Title } from "../styles";

import { CHART_CONFIG, EARNING_BARS, TIME_RANGES, initialMonthCount, initialStartDate } from "./constants";
import type { EarningsChartData } from "./types";
import { useEarningsChartData } from "./useTotalEarnings";

const calculateTotal = (payload: Record<string, unknown>) =>
  Object.entries(payload)
    .filter(([key]) => key !== "start_date" && !key.includes("_total_billed"))
    .reduce((sum, [, value]) => sum + parseFloat(value as string), 0);

const calculateTotalBilled = (payload: Record<string, unknown>) =>
  Object.entries(payload)
    .filter(([key]) => key !== "start_date" && key.includes("_total_billed"))
    .reduce((sum, [, value]) => sum + parseFloat(value as string), 0);

const CustomTooltip = (props: TooltipProps<ValueType, NameType>) => {
  const { t } = useTranslation();
  const { active, payload } = props;

  if (active && payload && payload.length) {
    const total = calculateTotal(payload[0].payload);
    const totalBilled = calculateTotalBilled(payload[0].payload);

    const newPayload = [
      ...payload,
      {
        name: t("earnings_widget.total_earnings.total"),
        value: total,
      },
      {
        name: t("earnings_widget.total_earnings.total_billed"),
        value: totalBilled,
      },
    ];
    return <DefaultTooltipContent {...props} payload={newPayload} />;
  }

  return null;
};

export const TotalEarnings: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { profile } = useProfileContext();
  const [monthCount, setMonthCount] = useState<number>(initialMonthCount);
  const [startDate, setStartDate] = useState(initialStartDate);

  invariant(profile, "Profile should be defined");

  const { data } = useGetEarningsUsTherapist({ therapist_id: profile.id, start_date: startDate });

  const chartData = useEarningsChartData(data);

  const timeRangeButtons = useMemo(
    () => [
      {
        value: String(TIME_RANGES.THREE_MONTHS),
        label: t("earnings_widget.total_earnings.buttons.last_3_months"),
      },
      {
        value: String(TIME_RANGES.SIX_MONTHS),
        label: t("earnings_widget.total_earnings.buttons.last_6_months"),
      },
      {
        value: String(TIME_RANGES.TWELVE_MONTHS),
        label: t("earnings_widget.total_earnings.buttons.last_12_months"),
      },
    ],
    [t]
  );

  const handleTimeRangeChange = useCallback((value: string) => {
    setMonthCount(Number(value));
  }, []);

  useEffect(() => {
    const start = startOfMonth(new Date());
    const subtractedStart = subMonths(start, monthCount);
    const formattedStart = format(subtractedStart, "yyyy-MM-dd");
    setStartDate(formattedStart);
  }, [monthCount]);

  const getBars = useCallback(() => {
    return EARNING_BARS.map(bar => {
      const hasData = chartData?.some(item => {
        const value = item[bar.dataKey as keyof EarningsChartData];
        return value !== undefined && parseFloat(value as string) > 0;
      });

      const legendType = hasData ? "rect" : "none";

      return (
        <Bar
          key={bar.dataKey}
          dataKey={bar.dataKey}
          name={t(`dashboard.tables.notes.types.${bar.translationKey}`)}
          stackId="a"
          fill={theme.colors.redesign[bar.colorKey]}
          legendType={legendType}
          shape={(shapeProps: unknown) => (
            <RoundedBar {...(shapeProps as Omit<RoundedBarProps, "bars">)} bars={EARNING_BARS} />
          )}
        />
      );
    });
  }, [chartData, t, theme]);

  return (
    <Container>
      <TopSection>
        <Box display="flex" gap="16px" alignItems="center">
          <Typography variant="h3">{t("earnings_widget.total_earnings.header")}</Typography>
          <Title>${data?.total && convertCentsToDollars(data.total)}</Title>
        </Box>
        <ButtonsGroup group={timeRangeButtons} active={String(monthCount)} setActive={handleTimeRangeChange} />
      </TopSection>
      <ResponsiveContainer width="100%" height={CHART_CONFIG.HEIGHT}>
        <BarChart
          width={CHART_CONFIG.WIDTH}
          height={CHART_CONFIG.CHART_HEIGHT}
          data={chartData}
          margin={CHART_CONFIG.MARGIN}
        >
          <CartesianGrid strokeDasharray="4 7" stroke-linecap="round" vertical={false} stroke="#D4DBE2" />
          <XAxis
            dataKey="start_date"
            tickFormatter={value => {
              const date = parseISO(value);
              return isValid(date) ? format(date, "MMM yy").toLocaleUpperCase() : "";
            }}
            stroke="#94A3B8"
          />
          <YAxis
            tickFormatter={value => `$${value}`}
            axisLine={false}
            tickLine={false}
            stroke="#17222F"
            fontSize="14px"
            fontWeight="600"
          />
          <Tooltip
            formatter={value => `$${value}`}
            labelFormatter={label => {
              const date = parseISO(label);
              return isValid(date) ? format(date, "MMMM yyyy") : "";
            }}
            cursor={(chartData && chartData?.length > 0) ?? false}
            content={props => <CustomTooltip {...props} />}
          />
          <Legend verticalAlign="bottom" wrapperStyle={{ paddingTop: 24 }} />
          {getBars()}
        </BarChart>
      </ResponsiveContainer>
    </Container>
  );
};

const TopSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;
